import React, { useState, useRef, useEffect } from 'react';
import { Camera, ImagePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CameraViewProps {
  onCapture: (dataUrl: string) => void;
  captureType: 'eyes' | 'nails';
  previewImage?: string | null;
  isLiveMode?: boolean;
  onLivePrediction?: (predictions: any[]) => void;
  inferenceEngine?: any;
  workerId?: string | null;
}

const CameraView: React.FC<CameraViewProps> = ({ 
  onCapture, 
  captureType, 
  previewImage, 
  isLiveMode = false, 
  onLivePrediction, 
  inferenceEngine, 
  workerId 
}) => {
  const { t } = useTranslation();
  const [imagePreview, setImagePreview] = useState<string | null>(previewImage || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [cameraStatus, setCameraStatus] = useState<'idle' | 'requesting' | 'streaming' | 'error'>('idle');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [livePredictions, setLivePredictions] = useState<any[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const lastInferenceTime = useRef<number>(0);
  const isInferenceActive = useRef<boolean>(false);
  const INFERENCE_INTERVAL = 1000; // Inferencia cada 1 segundo (1 FPS)
  const [autoStopEnabled, setAutoStopEnabled] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.3); // 30% de confianza (mismo que modelo 2)
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setImagePreview(dataUrl);
        onCapture(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (previewImage) {
      setImagePreview(previewImage);
    }
  }, [previewImage]);

  // Iniciar cámara para modo Live
  useEffect(() => {
    let mounted = true;
    
    const initializeCamera = async () => {
      if (isLiveMode && mounted) {
        // Iniciar cámara inmediatamente cuando se activa el modo Live
        await startCamera();
      } else if (!isLiveMode && isStreaming && mounted) {
        stopCamera();
      }
    };
    
    initializeCamera();
    
    return () => {
      mounted = false;
      stopCamera();
      stopLiveInference();
    };
  }, [isLiveMode]);

  // Iniciar inferencia cuando el motor esté listo
  useEffect(() => {
    if (isLiveMode && inferenceEngine && workerId && isStreaming) {
      startLiveInference();
    }
  }, [inferenceEngine, workerId, isStreaming]);

  // Cleanup cuando el componente se desmonte
  useEffect(() => {
    return () => {
      stopCamera();
      stopLiveInference();
    };
  }, []);

  const startCamera = async () => {
    try {
      console.log('Iniciando cámara...');
      setCameraStatus('requesting');
      setCameraError(null);
      
      // Esperar un poco para asegurar que el elemento video esté renderizado
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Verificar que el elemento video existe, si no, esperar un poco más
      let attempts = 0;
      const maxAttempts = 10;
      while (!videoRef.current && attempts < maxAttempts) {
        console.log(`Esperando elemento video... intento ${attempts + 1}`);
        await new Promise(resolve => setTimeout(resolve, 200));
        attempts++;
      }
      
      if (!videoRef.current) {
        console.error('Elemento video no disponible después de esperar');
        setCameraStatus('error');
        setCameraError('Elemento video no se pudo crear - problema de renderizado');
        return;
      }
      
      console.log('Elemento video encontrado, obteniendo stream...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 }, 
          height: { ideal: 480 },
          facingMode: 'user' // Usar cámara frontal por defecto
        } 
      });
      
      console.log('Stream obtenido:', stream);
      console.log('Video tracks:', stream.getVideoTracks());
      
      console.log('Asignando stream al video element...');
      videoRef.current.srcObject = stream;
      
      // Esperar a que el video esté listo
      videoRef.current.onloadedmetadata = () => {
        console.log('Metadata del video cargada');
        videoRef.current?.play().then(() => {
          console.log('Video playing');
          setIsStreaming(true);
          setCameraStatus('streaming');
          setCameraError(null);
          console.log('isStreaming establecido a true');
        }).catch((playError) => {
          console.error('Error al reproducir video:', playError);
          setCameraStatus('error');
          setCameraError(`Error al reproducir video: ${playError.message}`);
        });
      };
      
      videoRef.current.onerror = (error) => {
        console.error('Error en el video element:', error);
        setCameraStatus('error');
        setCameraError(`Error en elemento video: ${error}`);
      };
      
      console.log('Cámara iniciada correctamente');
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraStatus('error');
      
      // Capturar y mostrar mensaje de error específico
      if (error instanceof Error) {
        let errorMessage = '';
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Permisos de cámara denegados';
          console.error('Permisos de cámara denegados');
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No se encontró cámara';
          console.error('No se encontró cámara');
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'Cámara ya está en uso por otra aplicación';
          console.error('Cámara ya está en uso');
        } else if (error.name === 'OverconstrainedError') {
          errorMessage = 'Configuración de cámara no soportada';
          console.error('Configuración de cámara no soportada');
        } else if (error.name === 'SecurityError') {
          errorMessage = 'Error de seguridad - HTTPS requerido';
          console.error('Error de seguridad');
        } else {
          errorMessage = `Error desconocido: ${error.name} - ${error.message}`;
          console.error('Error desconocido:', error);
        }
        setCameraError(errorMessage);
      } else {
        setCameraError(`Error inesperado: ${String(error)}`);
      }
    }
  };

  const stopCamera = () => {
    // Detener inferencia en tiempo real
    stopLiveInference();
    
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsStreaming(false);
      setCameraStatus('idle');
      setCameraError(null);
    }
  };

  const startLiveInference = () => {
    if (isInferenceActive.current) {
      console.log('Inferencia ya está activa, no iniciar de nuevo');
      return; // Ya está activo, no iniciar de nuevo
    }
    
    if (!inferenceEngine || !workerId) {
      console.log('Motor de inferencia o worker no están listos');
      return;
    }
    
    console.log('Iniciando inferencia en tiempo real...');
    isInferenceActive.current = true;
    
    const runInference = async () => {
      if (!isStreaming || !inferenceEngine || !workerId || !videoRef.current || !isInferenceActive.current) {
        console.log('Deteniendo inferencia - condiciones no cumplidas');
        isInferenceActive.current = false;
        return;
      }

      const now = Date.now();
      
      // Solo hacer inferencia si ha pasado el tiempo suficiente
      if (now - lastInferenceTime.current >= INFERENCE_INTERVAL) {
        try {
          console.log('Ejecutando inferencia...');
          // Configuración de inferencia en tiempo real
          const inferenceConfig = {
            scoreThreshold: 0.3,    // Filtro de confianza (mismo que modelo 2)
            iouThreshold: 0.51,     // Filtro de solapamiento (mismo que modelo 2)
            maxNumBoxes: 20         // Máximo número de detecciones
          };
          const predictions = await inferenceEngine.infer(workerId, videoRef.current, inferenceConfig);
          console.log('Predicciones recibidas:', predictions);
          setLivePredictions(predictions);
          if (onLivePrediction) {
            onLivePrediction(predictions);
          }
          lastInferenceTime.current = now;
          
          // Auto-stop si se detecta algo con suficiente confianza
          if (autoStopEnabled && predictions.length > 0) {
            const highConfidenceDetection = predictions.find(
              (pred: any) => pred.confidence >= confidenceThreshold
            );
            
            if (highConfidenceDetection) {
              console.log('Auto-stopping due to high confidence detection:', highConfidenceDetection);
              // Detener cámara e inferencia automáticamente
              stopCamera();
              stopLiveInference();
              return;
            }
          }
        } catch (error) {
          console.error('Error in live inference:', error);
        }
      }

      // Continuar con la siguiente inferencia solo si estamos activos
      if (isInferenceActive.current && isStreaming) {
        animationFrameRef.current = requestAnimationFrame(runInference);
      } else {
        isInferenceActive.current = false;
      }
    };

    // Limpiar cualquier frame anterior
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    runInference();
  };

  const stopLiveInference = () => {
    isInferenceActive.current = false;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };
  
  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const instructions = captureType === 'eyes' 
    ? t('photoEvaluation.camera.eyeInstructions')
    : t('photoEvaluation.camera.nailInstructions');
  
  const placeholderImage = captureType === 'eyes'
    ? 'https://via.placeholder.com/300x200?text=Foto+de+Ojo'
    : 'https://via.placeholder.com/300x200?text=Foto+de+Uñas';
  
  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-2">
          {captureType === 'eyes' ? t('photoEvaluation.camera.eyeTitle') : t('photoEvaluation.camera.nailTitle')}
        </h3>
        <p className="text-gray-600 text-sm mb-4">{instructions}</p>
        
        <div 
          className="relative w-full h-64 bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center"
          onClick={!isLiveMode ? handleCameraClick : undefined}
        >
          {/* Debug info */}
          {isLiveMode && (
            <div className="absolute top-1 left-1 bg-black bg-opacity-50 text-white text-xs p-1 rounded z-50 max-w-xs">
              <div>Live: {isLiveMode ? 'true' : 'false'}</div>
              <div>Streaming: {isStreaming ? 'true' : 'false'}</div>
              <div>Status: {cameraStatus}</div>
              {cameraError && (
                <div className="text-red-300 mt-1">
                  Error: {cameraError}
                </div>
              )}
            </div>
          )}
          
          {isLiveMode ? (
            <div className="relative w-full h-full">
              <video 
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                playsInline
                style={{ display: isStreaming ? 'block' : 'none' }}
              />
              <canvas 
                ref={canvasRef}
                className="absolute top-0 left-0 w-full h-full pointer-events-none"
              />
              
              {/* Mensaje cuando el video está oculto */}
              {!isStreaming && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">
                      {cameraStatus === 'requesting' ? 'Solicitando acceso a la cámara...' :
                       cameraStatus === 'streaming' ? 'Cámara iniciada - Esperando motor de inferencia...' :
                       cameraStatus === 'error' ? 'Error al acceder a la cámara' :
                       'Iniciando cámara...'}
                    </p>
                    {cameraError && (
                      <p className="text-red-500 text-xs mt-2">
                        {cameraError}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
          {/* Overlay de predicciones */}
          {livePredictions.map((prediction, index) => {
            const isHighConfidence = prediction.confidence >= confidenceThreshold;
            return (
              <div
                key={index}
                className={`absolute border-2 ${
                  isHighConfidence 
                    ? 'border-green-500 bg-green-500 bg-opacity-30' 
                    : 'border-red-500 bg-red-500 bg-opacity-20'
                }`}
                style={{
                  left: `${prediction.x}px`,
                  top: `${prediction.y}px`,
                  width: `${prediction.width}px`,
                  height: `${prediction.height}px`
                }}
              >
                <span className={`absolute -top-6 left-0 text-xs px-1 rounded ${
                  isHighConfidence 
                    ? 'bg-green-500 text-white' 
                    : 'bg-red-500 text-white'
                }`}>
                  {prediction.class} ({Math.round(prediction.confidence * 100)}%)
                  {isHighConfidence && ' ✓'}
                </span>
              </div>
            );
          })}
          
          {/* Controles del modo Live */}
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            <button
              onClick={() => {
                stopCamera();
                stopLiveInference();
              }}
              className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
            >
              Detener
            </button>
            
            {/* Toggle auto-stop */}
            <button
              onClick={() => setAutoStopEnabled(!autoStopEnabled)}
              className={`px-2 py-1 rounded text-xs ${
                autoStopEnabled 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              }`}
            >
              {autoStopEnabled ? 'Auto-stop ON' : 'Auto-stop OFF'}
            </button>
          </div>
            </div>
          ) : imagePreview ? (
            <img 
              src={imagePreview} 
              alt={captureType === 'eyes' ? t('photoEvaluation.camera.eyeAlt') : t('photoEvaluation.camera.nailAlt')} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center p-4">
              <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">
                {isLiveMode ? (
                  cameraStatus === 'requesting' ? 'Solicitando acceso a la cámara...' :
                  cameraStatus === 'streaming' ? 'Cámara iniciada - Esperando motor de inferencia...' :
                  cameraStatus === 'error' ? 'Error al acceder a la cámara' :
                  'Iniciando cámara...'
                ) : t('photoEvaluation.camera.tapToCapture')}
              </p>
              {isLiveMode && cameraStatus === 'requesting' && (
                <p className="text-xs text-gray-400 mt-2">
                  Asegúrate de permitir el acceso a la cámara cuando se solicite
                </p>
              )}
              {isLiveMode && cameraStatus === 'error' && (
                <div className="text-xs text-red-400 mt-2">
                  <p>Error: {cameraError || 'Error desconocido'}</p>
                  <p className="mt-1">Revisa los permisos de cámara en tu navegador</p>
                </div>
              )}
            </div>
          )}
          
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
        
        {/* Solo mostrar "Load another image" si NO está en modo Live */}
        {!isLiveMode && (
          <div className="mt-2 flex justify-center">
            <button 
              className="flex items-center text-sm text-gray-500 hover:text-sangre-600"
              onClick={handleCameraClick}
            >
              <ImagePlus className="w-4 h-4 mr-1" />
              {t('photoEvaluation.camera.loadAnotherImage')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraView;
